// Copyright (c) Docugami, Inc. All rights reserved.

import { createFolderId } from "components/file_management/file_helpers";
import { TokenRange } from "networking/api";
import { useCallback, useMemo } from "react";
import { MultiSelectComponent } from "sdv/components/MultiSelectComponent";
import { TokenRangeComponent } from "sdv/components/TokenRangeComponent";
import { ComponentId } from "sdv/engine/Component";
import { Engine } from "sdv/engine/Engine";
import { MultiSelectChunk } from "sdv/Resources";
import { selectMappedTokens } from "store/dgml/dgmlSelectors";
import { SpanIdUtils } from "utils/SpanIdUtils";
import { DgmlInstancesSingleton } from "./documentDgml";
import { useAppSelector } from "./redux";
import { useSdvEntityQuery } from "./SdvEntityQuery";
import { useSdvResource } from "./SdvResource";

const multiSelectComponents = [ComponentId.MultiSelect, ComponentId.TokenRange];

export function useMultiSelectChunks(documentId: string, engine: Engine | null) {
    const { clusterId, fileId } = createFolderId(documentId)
    const mappedTokens = useAppSelector(state => selectMappedTokens(state, clusterId, fileId));

    const { set: setMultiSelect, value: multiSelectEnabled } = useSdvResource('multiSelect', engine);
    const { set: setSdvMultiSelectChunks } = useSdvResource('externalSelections', engine);

    // Only set the sdv multi-select chunks from when initiating multi-select mode.
    // This is to ensure the multi-selection chunks state does not change while actively editing.
    const startMultiSelect = useCallback((spanIds: string[]) => {
        const chunks: MultiSelectChunk[] = [];
        for (const spanId of spanIds) {
            const tokenRange = SpanIdUtils.getTokenRange(mappedTokens, SpanIdUtils.parse(spanId));
            if (!!tokenRange) {
                chunks.push({ spanId: spanId, tokenRange: tokenRange });
            }
        }
        setSdvMultiSelectChunks(chunks ?? []);
        setMultiSelect(true);
    }, [mappedTokens, setMultiSelect, setSdvMultiSelectChunks]);

    function completeMultiSelect() {
        // Reset multi-select state
        setSdvMultiSelectChunks([]);
        setMultiSelect(false);
    }

    const { entities } = useSdvEntityQuery(multiSelectComponents, engine);

    const [spanIds, spanToTokenRangeLookup] = useMemo((): [string[], { [id: string]: TokenRange }] => {
        if (!multiSelectEnabled || !DgmlInstancesSingleton.isInitialized(documentId)) {
            return [[], {}];
        }

        const spanIds: string[] = [];
        const lookup: { [id: string]: TokenRange } = {};

        for (const entity of entities) {
            let spanId = entity.getComponent<MultiSelectComponent>(ComponentId.MultiSelect)!.spanId
            let tokenRange = entity.getComponent<TokenRangeComponent>(ComponentId.TokenRange)!.tokenRange;
            spanIds.push(spanId);
            lookup[spanId] = tokenRange;
        }

        return [spanIds, lookup];
    }, [multiSelectEnabled, documentId, entities]);

    const removeMultiSelectChunk = useCallback((spanId: string) =>{
        const targetEntity = entities.find(e => e.getComponent<MultiSelectComponent>(ComponentId.MultiSelect)!.spanId === spanId);
        targetEntity?.destroy();
    }, [entities]);

    return {
        isActive: multiSelectEnabled,
        startMultiSelect,
        stopMultiSelect: completeMultiSelect,
        spanIds: spanIds,
        spanToTokenRangeLookup,
        removeMultiSelectChunk
    }
}
